/** FINANCE VAULT — sections: overview, document explorer, bank matrix,
 *  packs, missing, history, configuration. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, FileDown, Landmark, Plus, CheckCircle2, PackagePlus, Pencil } from "lucide-react";
import {
  PreviewDialog, DetailsDialog, BankAccountDialog, PackDialog,
} from "./FinanceVaultDialogs";

const inputCls = "text-[11px] border border-slate-300 rounded-md px-2 py-1.5";
export const ENTITY_BADGE = {
  SUNTRONIX_CORPORATE: "bg-blue-100 text-blue-900",
  PERSONAL: "bg-purple-100 text-purple-900",
};
const STATUS_BADGE = {
  CURRENT: "bg-emerald-100 text-emerald-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
  SUPERSEDED: "bg-slate-200 text-slate-600",
  INACTIVE: "bg-slate-100 text-slate-400",
};
const stat = (v) => v === "AVAILABLE"
  ? <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">AVAILABLE</span>
  : <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">PENDING</span>;

// ---------------------------------------------------------------- OVERVIEW
export function Overview({ fy, access, refreshKey, goto }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/finance-vault/overview", { params: { fy } })
      .then(({ data }) => setD(data));
  }, [fy, refreshKey]);
  const shortcuts = [
    ...(access.corporate ? [["SUNTRONIX CORPORATE", "SUNTRONIX CORPORATE"]] : []),
    ...(access.personal ? [["PERSONAL", "PERSONAL"]] : []),
    ["BANK STATEMENTS", "BANK STATEMENTS"], ["ITR", "ITR / INCOME TAX"],
    ["BALANCE SHEET", "BALANCE SHEETS"], ["PROFIT & LOSS", "PROFIT & LOSS"],
    ["CA DOCUMENTS", "CA / AUDITOR"], ["MISSING DOCUMENTS", "MISSING DOCUMENTS"],
    ["DOCUMENT PACKS", "DOCUMENT PACKS"]];
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="fv-overview">
      <div className="flex flex-wrap gap-1.5">
        {shortcuts.map(([label, target]) => (
          <button key={label} onClick={() => goto(target)}
              data-testid={`fv-shortcut-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className="text-[10px] font-semibold px-2.5 py-1.5 rounded-full border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors">
            {label}</button>))}
      </div>
      {d.corporate && <EntityCard title="SUNTRONIX CORPORATE" data={d.corporate} testid="fv-ov-corporate" />}
      {d.personal && <EntityCard title="PERSONAL" data={d.personal} testid="fv-ov-personal" />}
      <div className="flex gap-3 text-[11px] text-slate-500">
        <span data-testid="fv-ov-missing">Missing documents: <b className="text-rose-700">{d.missing_count}</b></span>
        <span>Packs: <b>{d.packs}</b></span>
      </div>
    </div>
  );
}

function EntityCard({ title, data, testid }) {
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={testid}>
      <div className="text-[11px] font-heading font-semibold mb-3">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-[11px]">
        <Kpi label="ITR">{stat(data.itr)}</Kpi>
        <Kpi label="Computation">{stat(data.computation)}</Kpi>
        <Kpi label="Balance Sheet">{stat(data.balance_sheet)}</Kpi>
        <Kpi label="P&L">{stat(data.pnl)}</Kpi>
        <Kpi label="Bank Statements">
          <span className="font-mono">{data.bank_statements.available}/{data.bank_statements.expected}</span></Kpi>
        <Kpi label="CA Documents"><span className="font-mono">{data.ca_documents}</span></Kpi>
        <Kpi label="Documents"><span className="font-mono">{data.documents}</span>
          {data.review_required > 0 && <span className="ml-1 text-[9px] px-1 rounded bg-amber-100 text-amber-800">
            {data.review_required} review</span>}</Kpi>
      </div>
    </div>
  );
}
const Kpi = ({ label, children }) => (
  <div><div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>{children}</div>);

// ---------------------------------------------------------- DOC EXPLORER
export function DocExplorer({ fy, access, refreshKey, bump, openUpload,
                              entity, types, caTagged, caMode, title }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [allFy, setAllFy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [details, setDetails] = useState(null);
  const [editDoc, setEditDoc] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (entity) params.entity = entity;
    if (types) params.types = types.join(",");
    if (caTagged && !q) params.ca_tagged = true;
    if (!allFy && !q) params.financial_year = fy;
    api.get("/finance-vault/documents", { params })
      .then(({ data }) => setItems(data.items || []));
  }, [q, entity, types, caTagged, allFy, fy]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); },
    [load, refreshKey, q]);

  const verify = async (doc) => {
    try {
      await api.post(`/finance-vault/documents/${doc.id}/verify`);
      toast.success("Document verified"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const download = async (doc) => {
    const get = (reason) => api.get(`/finance-vault/documents/${doc.id}/download`,
      { responseType: "blob", params: reason ? { reason } : {} });
    try {
      let res;
      try { res = await get(); }
      catch (e) {
        let detail = e.response?.data;
        if (detail instanceof Blob) detail = JSON.parse(await detail.text());
        if (detail?.detail?.code === "REASON_REQUIRED") {
          const reason = window.prompt("This document requires a download reason (audited):");
          if (!reason) return;
          res = await get(reason);
        } else throw e;
      }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename || doc.title; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-3" data-testid="fv-doc-explorer">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">{title}</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fv-search"
            placeholder="Search title, bank, month, FY…" className={inputCls + " w-64"} />
        <label className="text-[10px] text-slate-500 inline-flex items-center gap-1">
          <input type="checkbox" checked={allFy} onChange={(e) => setAllFy(e.target.checked)}
              data-testid="fv-all-fy" /> All years</label>
        <button onClick={() => openUpload({ entity, financial_year: fy,
            doc_type: types?.length === 1 ? types[0] : undefined })}
            data-testid="fv-explorer-upload"
            className="text-[10.5px] px-2 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> Upload here</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Document</th>
            <th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">FY / AY</th>
            <th className="text-left px-3 py-2">Bank / Month</th>
            {caMode && <th className="text-left px-3 py-2">CA tag</th>}
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Uploaded</th>
            <th className="text-right px-3 py-2 w-40"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 px-3 py-8">
              No documents yet in this view.</td></tr>}
            {items.map((doc) => (
              <tr key={doc.id} className="border-t border-slate-100" data-testid={`fv-doc-${doc.id}`}>
                <td className="px-3 py-2">
                  <button onClick={() => setDetails(doc)} data-testid={`fv-doc-title-${doc.id}`}
                      className="font-medium text-left hover:underline">{doc.title}</button>
                  <div className="text-[9px] text-slate-400">{doc.filename} · v{doc.version}</div></td>
                <td className="px-3 py-2"><span className={"text-[8.5px] font-semibold px-1.5 py-0.5 rounded " +
                    (ENTITY_BADGE[doc.entity] || "")}>{doc.entity === "PERSONAL" ? "PERSONAL" : "SUNTRONIX"}</span></td>
                <td className="px-3 py-2 text-[10px]">{doc.doc_type.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 font-mono text-[10px]">{doc.financial_year || "—"}
                  {doc.assessment_year && <div className="text-slate-400">{doc.assessment_year}</div>}</td>
                <td className="px-3 py-2 text-[10px]">{doc.institution || "—"}
                  {doc.month && <span className="ml-1 font-mono">{doc.month}</span>}</td>
                {caMode && <td className="px-3 py-2 text-[10px]">
                  {doc.ca_tag ? doc.ca_tag.replace(/_/g, " ") : "—"}
                  {doc.ca_name && <div className="text-slate-400">{doc.ca_name}</div>}</td>}
                <td className="px-3 py-2"><span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
                    (STATUS_BADGE[doc.status] || "")}>{doc.status.replace(/_/g, " ")}</span>
                  {doc.verified_on && <div className="text-[8.5px] text-emerald-700">verified</div>}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{(doc.uploaded_on || "").slice(0, 10)}
                  <div className="text-[9px] text-slate-400">{doc.uploaded_by}</div></td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => setPreview(doc)} title="Preview" data-testid={`fv-preview-${doc.id}`}
                      className="p-1 rounded border border-slate-200"><Eye className="h-3 w-3" /></button>
                  <button onClick={() => download(doc)} title="Download" data-testid={`fv-download-${doc.id}`}
                      className="p-1 rounded border border-slate-200"><FileDown className="h-3 w-3" /></button>
                  {access.can_edit !== false && (
                    <button onClick={() => setEditDoc(doc)} title="Edit metadata" data-testid={`fv-edit-${doc.id}`}
                        className="p-1 rounded border border-slate-200"><Pencil className="h-3 w-3" /></button>)}
                  {!doc.verified_on && (
                    <button onClick={() => verify(doc)} title="Verify" data-testid={`fv-verify-${doc.id}`}
                        className="p-1 rounded border border-emerald-300 text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /></button>)}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
      {editDoc && <DocEditDialog doc={editDoc} access={access}
          onClose={() => setEditDoc(null)} onDone={() => { setEditDoc(null); load(); bump(); }} />}
      {details && <DetailsDialog doc={details} access={access} openUpload={openUpload}
          onClose={() => setDetails(null)} onDone={() => { setDetails(null); bump(); }} />}
    </div>
  );
}

// Lightweight metadata editor for a vault document (PATCH /documents/{id}).
function DocEditDialog({ doc, access, onClose, onDone }) {
  const [f, setF] = useState({
    title: doc.title || "", doc_type: doc.doc_type || "", institution: doc.institution || "",
    month: doc.month || "", financial_year: doc.financial_year || "", notes: doc.notes || "",
    status: doc.status || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/finance-vault/documents/${doc.id}`, f);
      toast.success("Document updated"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="fv-doc-edit-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold">Edit document</div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <label className="col-span-2 text-[11px] text-slate-500">Title
            <input value={f.title} onChange={set("title")} data-testid="fv-edit-title" className={inputCls + " w-full mt-1"} /></label>
          <label className="text-[11px] text-slate-500">Type
            <select value={f.doc_type} onChange={set("doc_type")} data-testid="fv-edit-doctype" className={inputCls + " w-full mt-1"}>
              {(access.doc_types || []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select></label>
          <label className="text-[11px] text-slate-500">Institution / Bank
            <input value={f.institution} onChange={set("institution")} className={inputCls + " w-full mt-1"} /></label>
          <label className="text-[11px] text-slate-500">Financial year
            <input value={f.financial_year} onChange={set("financial_year")} placeholder="2025-26" className={inputCls + " w-full mt-1"} /></label>
          <label className="text-[11px] text-slate-500">Month
            <input value={f.month} onChange={set("month")} placeholder="APR" className={inputCls + " w-full mt-1"} /></label>
          <label className="col-span-2 text-[11px] text-slate-500">Notes
            <input value={f.notes} onChange={set("notes")} className={inputCls + " w-full mt-1"} /></label>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-300">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fv-edit-save"
              className="text-[12px] px-3 py-1.5 rounded-md bg-emerald-700 text-white">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- BANK SECTION
export function BankSection({ fy, access, refreshKey, bump, openUpload }) {
  const [accounts, setAccounts] = useState([]);
  const [sel, setSel] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/finance-vault/bank-accounts").then(({ data }) => {
      setAccounts(data.items || []);
      if (!sel && data.items?.length) setSel(data.items[0].id);
    });
  }, [refreshKey]); // eslint-disable-line
  useEffect(() => {
    if (!sel) { setMatrix(null); return; }
    api.get(`/finance-vault/bank-accounts/${sel}/matrix`, { params: { fy } })
      .then(({ data }) => setMatrix(data));
  }, [sel, fy, refreshKey]);

  const MSTAT = { AVAILABLE: "bg-emerald-100 text-emerald-800 border-emerald-300",
    MISSING: "bg-rose-100 text-rose-800 border-rose-300",
    NOT_APPLICABLE: "bg-slate-100 text-slate-400 border-slate-200",
    UPCOMING: "bg-white text-slate-300 border-slate-200" };

  return (
    <div className="space-y-4" data-testid="fv-bank-section">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[12px] font-semibold flex-1">Bank Accounts & Monthly Statements</div>
        <button onClick={() => setAddOpen(true)} data-testid="fv-add-bank-account"
            className="text-[10.5px] px-2 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <Landmark className="h-3 w-3" /> Add bank account</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {accounts.map((a) => (
          <button key={a.id} onClick={() => setSel(a.id)} data-testid={`fv-bank-${a.id}`}
              className={"text-[10.5px] px-2.5 py-1.5 rounded-lg border text-left " +
                (sel === a.id ? "bg-slate-900 text-white border-slate-900"
                              : "border-slate-300 hover:bg-slate-50")}>
            <div className="font-semibold">{a.bank_name}</div>
            <div className={"font-mono text-[9px] " + (sel === a.id ? "text-slate-300" : "text-slate-500")}>
              {a.account_masked || "—"} · {a.entity === "PERSONAL" ? "PERSONAL" : "SUNTRONIX"}</div>
          </button>))}
        {accounts.length === 0 && <span className="text-[11px] text-slate-400">
          No bank accounts yet — add one to build the monthly statement matrix.</span>}
      </div>
      {matrix && (
        <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid="fv-statement-matrix">
          <div className="text-[11px] font-semibold mb-3">
            {matrix.account.bank_name} <span className="font-mono text-slate-500">{matrix.account.account_masked}</span>
            <span className="text-slate-400 font-normal"> · {fy} monthly statements</span></div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
            {matrix.months.map((m) => (
              <MonthCell key={m.month} m={m} cls={MSTAT[m.status]}
                  onUpload={() => openUpload({ entity: matrix.account.entity,
                    doc_type: "BANK_STATEMENT", financial_year: fy, month: m.month,
                    bank_account_id: matrix.account.id,
                    institution: matrix.account.bank_name,
                    title: `${matrix.account.bank_name} ${m.month} ${fy} statement` })}
                  onPreview={(doc) => setPreview(doc)} />))}
          </div>
          <div className="text-[9.5px] text-slate-400 mt-2">
            Click a month to upload / view. ✓ available · missing months highlighted red.</div>
        </div>)}
      {addOpen && <BankAccountDialog access={access} onClose={() => setAddOpen(false)}
          onDone={() => { setAddOpen(false); bump(); }} />}
      {preview && <PreviewDialog doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function MonthCell({ m, cls, onUpload, onPreview }) {
  const doc = m.documents[0];
  return (
    <button onClick={() => (doc ? onPreview(doc) : m.status !== "NOT_APPLICABLE" && onUpload())}
        data-testid={`fv-month-${m.month}`}
        className={"border rounded-md px-1 py-2 text-center " + cls}>
      <div className="text-[10px] font-bold">{m.month}</div>
      <div className="text-[8.5px] font-semibold">
        {m.status === "AVAILABLE" ? "✓" : m.status === "MISSING" ? "MISSING"
          : m.status === "NOT_APPLICABLE" ? "N/A" : "—"}</div>
    </button>
  );
}

// ------------------------------------------------------------------ PACKS
export function PacksSection({ fy, access, refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/finance-vault/packs").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  const dl = async (p) => {
    try {
      const res = await api.get(`/finance-vault/packs/${p.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.title}.zip`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="space-y-3" data-testid="fv-packs">
      <div className="flex items-center gap-2">
        <div className="text-[12px] font-semibold flex-1">Document Packs</div>
        <button onClick={() => setOpen(true)} data-testid="fv-create-pack"
            className="text-[10.5px] px-2 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
          <PackagePlus className="h-3 w-3" /> Create Financial Pack</button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Pack</th><th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">FY</th><th className="text-right px-3 py-2">Documents</th>
            <th className="text-left px-3 py-2">Created</th><th className="text-right px-3 py-2 w-24"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-8">
              No packs yet — build an organised ZIP of a financial year's documents.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`fv-pack-${p.id}`}>
                <td className="px-3 py-2 font-medium">{p.title}</td>
                <td className="px-3 py-2"><span className={"text-[8.5px] font-semibold px-1.5 py-0.5 rounded " +
                    (ENTITY_BADGE[p.entity] || "")}>{p.entity === "PERSONAL" ? "PERSONAL" : "SUNTRONIX"}</span></td>
                <td className="px-3 py-2 font-mono text-[10px]">{p.financial_year}</td>
                <td className="px-3 py-2 text-right font-mono">{p.doc_count}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{(p.created_at || "").slice(0, 10)} · {p.created_by}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => dl(p)} data-testid={`fv-pack-dl-${p.id}`}
                      className="text-[10px] px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <FileDown className="h-3 w-3" /> ZIP</button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {open && <PackDialog access={access} fy={fy} onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); bump(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- MISSING
export function MissingSection({ fy, refreshKey, openUpload }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/finance-vault/missing", { params: { fy } })
      .then(({ data }) => setItems(data.items || []));
  }, [fy, refreshKey]);
  return (
    <div className="space-y-3" data-testid="fv-missing">
      <div className="text-[12px] font-semibold">Missing documents — {fy}</div>
      {items.length === 0 && <div className="text-[11.5px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-4">
        Nothing missing for {fy} based on configured expectations.</div>}
      <div className="space-y-1.5">
        {items.map((m, i) => (
          <div key={i} className="flex items-center gap-2 border border-rose-200 bg-rose-50/60 rounded-lg px-3 py-2"
              data-testid={`fv-missing-${i}`}>
            <span className={"text-[8.5px] font-semibold px-1.5 py-0.5 rounded " + (ENTITY_BADGE[m.entity] || "")}>
              {m.entity === "PERSONAL" ? "PERSONAL" : "SUNTRONIX"}</span>
            <span className="text-[11.5px] flex-1">{m.label}</span>
            <button onClick={() => openUpload(m.kind === "MONTHLY_STATEMENT"
                ? { entity: m.entity, doc_type: "BANK_STATEMENT", financial_year: fy,
                    month: m.month, bank_account_id: m.bank_account_id, institution: m.bank,
                    title: `${m.bank} ${m.month} ${fy} statement` }
                : { entity: m.entity, doc_type: m.doc_type, financial_year: fy })}
                data-testid={`fv-missing-upload-${i}`}
                className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">Upload</button>
          </div>))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- HISTORY
export function HistorySection({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/finance-vault/audit").then(({ data }) => setItems(data.items || []));
  }, [refreshKey]);
  return (
    <div className="space-y-3" data-testid="fv-history">
      <div className="text-[12px] font-semibold">History — audited vault activity</div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Action</th>
            <th className="text-left px-3 py-2">Entity</th><th className="text-left px-3 py-2">Document</th>
            <th className="text-left px-3 py-2">By</th></tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-[10px]">{(e.at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="px-3 py-1.5 text-[10px] font-semibold">{e.action.replace(/_/g, " ")}</td>
                <td className="px-3 py-1.5 text-[10px]">{e.entity ? (e.entity === "PERSONAL" ? "PERSONAL" : "SUNTRONIX") : "—"}</td>
                <td className="px-3 py-1.5 text-[10px]">{e.document_title || "—"}</td>
                <td className="px-3 py-1.5 text-[10px] text-slate-500">{e.actor}</td>
              </tr>))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 px-3 py-8">
              No activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ CONFIGURATION
export function ConfigSection({ bump }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    api.get("/finance-vault/config").then(({ data }) => setCfg(data.config));
  }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    try {
      const { data } = await api.put("/finance-vault/config", patch);
      setCfg(data.config); toast.success("Configuration saved"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="fv-config">
      <div className="text-[12px] font-semibold">Finance Vault configuration</div>
      <ListEditor label="Document types" values={cfg.doc_types} testid="fv-cfg-doctypes"
          onSave={(v) => save({ doc_types: v })} mono />
      <ListEditor label="Corporate access roles" values={cfg.corporate_roles} testid="fv-cfg-roles"
          onSave={(v) => save({ corporate_roles: v })} mono
          hint="Roles allowed into Suntronix Corporate finance (super_admin always included)." />
      <ListEditor label="Personal Finance owners (emails)" values={cfg.personal_owner_emails}
          testid="fv-cfg-owners" onSave={(v) => save({ personal_owner_emails: v })}
          hint="Only these users can use the PERSONAL area. Each owner sees only their own documents." />
      <ListEditor label="Expected annual — SUNTRONIX CORPORATE"
          values={cfg.expected_annual.SUNTRONIX_CORPORATE} mono testid="fv-cfg-annual-corp"
          onSave={(v) => save({ expected_annual: { ...cfg.expected_annual, SUNTRONIX_CORPORATE: v } })} />
      <ListEditor label="Expected annual — PERSONAL"
          values={cfg.expected_annual.PERSONAL} mono testid="fv-cfg-annual-personal"
          onSave={(v) => save({ expected_annual: { ...cfg.expected_annual, PERSONAL: v } })} />
    </div>
  );
}

function ListEditor({ label, values, onSave, hint, mono, testid }) {
  const [text, setText] = useState((values || []).join("\n"));
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3">
      <div className="text-[10.5px] font-semibold mb-1">{label}</div>
      {hint && <div className="text-[9.5px] text-slate-400 mb-1">{hint}</div>}
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={Math.min(8, (values || []).length + 2)}
          data-testid={testid}
          className={"w-full text-[11px] border border-slate-200 rounded-md px-2 py-1.5 " + (mono ? "font-mono" : "")} />
      <div className="text-right mt-1">
        <button onClick={() => onSave(text.split("\n").map((s) => s.trim()).filter(Boolean))}
            data-testid={`${testid}-save`}
            className="text-[10px] px-2.5 py-1 rounded bg-slate-900 text-white">Save</button>
      </div>
    </div>
  );
}
